import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { validatePliToken, publicUploadPli } from "../services/pliService";

const card = {
  maxWidth: 520,
  margin: "60px auto",
  padding: 32,
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  fontFamily: "Arial, sans-serif",
  color: "#222",
};
const btn = {
  background: "#0b2545",
  color: "#fff",
  border: "none",
  padding: "12px 20px",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 15,
};

const Notice = ({ title, children }) => (
  <div style={card}>
    <h2 style={{ marginTop: 0 }}>{title}</h2>
    <p>{children}</p>
  </div>
);

const ContractorPliUploadPublic = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null); // { contractor_name, pli_expiry_date }
  const [reason, setReason] = useState(null); // expired | used | already_submitted | not_found
  const [form, setForm] = useState({ file: null, expiry_date: "", issue_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await validatePliToken(token);
        if (res.success) setInfo(res.data);
        else setReason(res.reason || "not_found");
      } catch {
        setReason("not_found");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.file) return setError("Please choose your certificate file.");
    // Keep in step with the backend (config/multer.js uploadPliCertificate): PDF/JPG/PNG, 10MB.
    const okTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!okTypes.includes(form.file.type)) {
      return setError("Please upload your certificate as a PDF, JPG or PNG.");
    }
    if (form.file.size > 10 * 1024 * 1024) {
      return setError("That file is larger than 10MB. Please upload a smaller file.");
    }
    if (!form.expiry_date) return setError("Please enter the certificate expiry date.");
    // Sanity check only: an already-expired certificate is almost always the OLD one
    // uploaded by mistake. Certificates expiring soon are allowed — the daily job will
    // raise a renewal reminder for anything expiring within 28 days.
    const days = Math.ceil((new Date(form.expiry_date) - new Date()) / 86400000);
    if (days <= 0) return setError("That certificate has already expired. Please upload your current certificate.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("document", form.file);
      fd.append("expiry_date", form.expiry_date);
      if (form.issue_date) fd.append("issue_date", form.issue_date);
      fd.append("name", "PLI Certificate");
      const res = await publicUploadPli(token, fd);
      if (res.success) setDone(true);
      else if (res.reason === "already_submitted") setReason("already_submitted");
      else setError(res.message || "Upload failed. Please try again.");
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Notice title="Loading…">One moment.</Notice>;

  if (reason === "expired")
    return <Notice title="This link has expired">Please contact Global Guardians to request a new upload link.</Notice>;
  if (reason === "used" || reason === "already_submitted")
    return <Notice title="Already submitted">A certificate has already been submitted for this contractor and is under review — no further action is needed. Thank you.</Notice>;
  if (reason === "not_found")
    return <Notice title="Invalid link">This upload link is not valid. Please contact Global Guardians.</Notice>;

  if (done)
    return <Notice title="Thank you">We've received your Public Liability Insurance certificate. It's now under review — no further action is needed unless we contact you.</Notice>;

  return (
    <div style={card}>
      <h2 style={{ marginTop: 0 }}>Upload your Public Liability Insurance</h2>
      {info?.contractor_name && <p><strong>{info.contractor_name}</strong></p>}
      <p>Please upload your current PLI certificate and confirm its dates.</p>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", marginBottom: 4 }}>Certificate (PDF, JPG or PNG — max 10MB)</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", marginBottom: 4 }}>Expiry date *</label>
          <input type="date" value={form.expiry_date} required
            onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", marginBottom: 4 }}>Issue date (optional)</label>
          <input type="date" value={form.issue_date}
            onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} />
        </div>
        {error && <p style={{ color: "#d33" }}>{error}</p>}
        <button type="submit" style={{ ...btn, opacity: submitting ? 0.6 : 1 }} disabled={submitting}>
          {submitting ? "Uploading…" : "Upload certificate"}
        </button>
      </form>
    </div>
  );
};

export default ContractorPliUploadPublic;
